<script setup>
    import { useLocationStore } from '~/stores/location';
    import { useUserStore } from '~/stores/user';

    const locationStore = useLocationStore();
    const userStore = useUserStore();
    const watch_id = ref(null);

    // watchPosition on a phone fires continuously, and a few metres of GPS jitter
    // is enough to rewrite every site's distance, re-order the list, and change
    // the hash the listing is keyed on — which remounts every card and re-requests
    // every image. That is the flashing, and the distances visibly ticking, that
    // Grace reported from an iPhone (Asana O2-25).
    //
    // A fix has to move further than the accuracy we can actually resolve before
    // it is worth republishing.
    const MIN_MOVE_METRES = 25;
    const lastFix = ref(null);

    const metresBetween = (a, b) => {
        const R = 6371000;
        const lat1 = (a.latitude * Math.PI) / 180;
        const lat2 = (b.latitude * Math.PI) / 180;
        const dLat = lat2 - lat1;
        const dLon = ((b.longitude - a.longitude) * Math.PI) / 180;
        const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
        return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
    };

    const commitCoords = (coords) => {
        // Any successful fix clears a previous refusal, including one the jitter
        // guard below is about to discard — the guard is about the coordinates
        // being uninteresting, not about permission.
        locationStore.setDenied(false);
        if (lastFix.value && metresBetween(lastFix.value, coords) < MIN_MOVE_METRES) return;
        lastFix.value = coords;
        locationStore.setCoords(coords);
    };

    const startTracking = () => {
        if (!('geolocation' in navigator)) {
            console.log('Geolocation not available');
            return;
        }

        if (watch_id.value !== null) {
            navigator.geolocation.clearWatch(watch_id.value);
            watch_id.value = null;
        }

        watch_id.value = navigator.geolocation.watchPosition(
            (position) => {
                commitCoords({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                });
            },
            (error) => {
                console.log('Error getting location:', error);
                // A watch that times out or briefly loses signal is not a reason to
                // throw away a good fix: clearing it emptied the Near Me list and
                // reset every distance mid-browse. Only an actual denial should.
                if (error?.code === 1) {
                    lastFix.value = null;
                    locationStore.setCoords(null);
                    locationStore.setDenied(true);
                }
            },
            {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0,
            }
        );
    };

    const stopTracking = () => {
        lastFix.value = null;
        locationStore.setCoords(null);
        if (watch_id.value !== null) {
            navigator.geolocation.clearWatch(watch_id.value);
            watch_id.value = null;
        }
    };

    const getPosition = () => {
        if (!('geolocation' in navigator)) {
            console.error('Geolocation not available');
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (position) => {
                commitCoords({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                });
            },
            (error) => {
                console.log('Error getting location:', error);
                if (error?.code === 1) {
                    lastFix.value = null;
                    locationStore.setCoords(null);
                    locationStore.setDenied(true);
                }
            },
            {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 30000,
            }
        );
    };

    // Ask the browser directly. Geolocation shows its own permission prompt, so
    // the Permissions API is only ever an optimisation here — never a gate.
    const requestLocation = () => {
        getPosition();
        startTracking();
    };

    // The map's Locate Me control asks for a fix through the store rather than
    // calling geolocation itself, so this component stays the only caller and the
    // single watch is never duplicated.
    //
    // maximumAge:30000 on getPosition means a press can be answered from a cached
    // fix; that is the right trade for a button whose job is to re-centre, and a
    // live watch is already running underneath it.
    watch(
        () => locationStore.requestNonce,
        () => requestLocation(),
    );

    onMounted(() => {
        // Safari, iPhone included, ships the Permissions API *without* a
        // geolocation descriptor: `query({ name: 'geolocation' })` rejects with a
        // TypeError instead of resolving. That rejection was unhandled, so the
        // whole handler died before requesting anything — `coords` stayed null and
        // every distance-dependent feature came back empty on iOS while working
        // fine in Chrome. Near Me showed "Sorry, no sites found!", the distance
        // sort silently left the list in A-to-Z order, and the map never centred
        // on you (Asana O2-23, O2-41, O2-45).
        if (!navigator.permissions?.query) {
            requestLocation();
            return;
        }

        navigator.permissions.query({ name: 'geolocation' }).then((result) => {
            if (result.state === 'granted') {
                userStore.setHasLocationPermission(true);
                startTracking();
            } else if (result.state === 'prompt') {
                getPosition();
                startTracking();
            } else if (result.state === 'denied') {
                console.log('Geolocation permission denied');
                stopTracking();
                userStore.setHasLocationPermission(false);                    
            }
            result.onchange = () => {
                if (result.state === 'granted') {
                    userStore.setHasLocationPermission(true);
                    getPosition();
                    startTracking();
                } else {
                    stopTracking();
                    userStore.setHasLocationPermission(false);
                }
            };
        }).catch(() => {
            // Unsupported descriptor (Safari) — fall back to asking outright.
            requestLocation();
        });
    });

    onBeforeUnmount(() => {
        stopTracking();
    });

</script>

<template>
    <div class="location">
        <!-- Loaded User Location -->
    </div>
</template>